import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import type { ReguideStep, ReguideTransitionContext } from 'reguide';
import { createPortal } from 'react-dom';
import { useAuth } from '#stores/AuthContext';
import {
  GUIDE_STEPS,
  reguideContext,
  type DashboardGuideTargetId,
} from '#stores/ReguideContext';
import ConfirmActionModal from '#components/modals/ConfirmActionModal';
import logger from '#utils/logger';
import HelpButton from './HelpButton';

const AUTO_OPEN_KEY = 'znario:reguide:dashboard:auto-opened';
const PERSISTENCE_KEY = 'znario:reguide:dashboard';
const { Provider: ReguideProvider } = reguideContext;

export type GuideWorkflow = {
  screen: 'library' | 'preview' | 'setup';
  hasSession: boolean;
  busy: boolean;
  hasScenarios: boolean;
  canStartSession: boolean;
  closePreview: () => void;
  cancelSetup: (
    destination: 'preview' | 'library',
    signal?: AbortSignal
  ) => Promise<void>;
};

type GuideLifecycle = {
  markPageLoading: () => void;
  markPageReady: () => void;
  registerWorkflow: (workflow: GuideWorkflow) => () => void;
};

const GuideLifecycleContext = createContext<GuideLifecycle | null>(null);

const GuideRuntime = ({
  children,
  enabled,
  pageReady,
  workflow,
  portalContainer,
  onOpenChange,
}: PropsWithChildren<{
  enabled: boolean;
  pageReady: boolean;
  workflow: GuideWorkflow | null;
  portalContainer: HTMLDialogElement | null;
  onOpenChange: (open: boolean) => void;
}>) => {
  const guide = reguideContext.useReguide();
  const { isOpen, currentStep, isTransitionPending, goToStepById, stop } =
    guide;

  useEffect(() => onOpenChange(isOpen), [isOpen, onOpenChange]);

  useEffect(() => {
    if (!enabled && isOpen) stop();
  }, [enabled, isOpen, stop]);

  useEffect(() => {
    if (!enabled || !pageReady || !workflow) return;
    if (localStorage.getItem(AUTO_OPEN_KEY)) return;
    localStorage.setItem(AUTO_OPEN_KEY, 'true');
    guide.start();
  }, [enabled, guide, pageReady, workflow]);

  useEffect(() => {
    if (!isOpen || !workflow || isTransitionPending || workflow.busy) return;
    const step = currentStep?.id;
    if (
      workflow.screen === 'library' &&
      workflow.hasScenarios &&
      (step === GUIDE_STEPS.SCENARIO_PREVIEW ||
        step === GUIDE_STEPS.SCENARIO_LAUNCH)
    ) {
      goToStepById(GUIDE_STEPS.SCENARIO_CARDS);
    } else if (
      workflow.screen === 'preview' &&
      step !== GUIDE_STEPS.SCENARIO_PREVIEW &&
      !(step === GUIDE_STEPS.SCENARIO_LAUNCH && !workflow.canStartSession)
    ) {
      goToStepById(GUIDE_STEPS.SCENARIO_PREVIEW);
    } else if (
      workflow.screen === 'setup' &&
      step !== GUIDE_STEPS.SCENARIO_LAUNCH
    ) {
      goToStepById(GUIDE_STEPS.SCENARIO_LAUNCH);
    }
  }, [currentStep?.id, goToStepById, isOpen, isTransitionPending, workflow]);

  useEffect(() => {
    const layer = document.querySelector<HTMLElement>('.reguide-layer');
    if (!layer || !isOpen) return undefined;
    layer.setAttribute('popover', 'manual');
    if (layer.matches(':popover-open')) layer.hidePopover();
    layer.showPopover();
    return () => {
      if (layer.matches(':popover-open')) layer.hidePopover();
    };
  }, [currentStep?.id, isOpen, portalContainer]);

  return (
    <>
      {children}
      {enabled &&
        createPortal(
          <HelpButton onClick={() => guide.restart()} />,
          portalContainer ?? document.body
        )}
    </>
  );
};

const GuideProvider = ({
  children,
  enabled,
}: PropsWithChildren<{ enabled: boolean }>) => {
  const { user } = useAuth();
  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia('(min-width: 768px)').matches
  );
  const [pageReady, setPageReady] = useState(false);
  const [workflow, setWorkflow] = useState<GuideWorkflow | null>(null);
  const workflowRef = useRef<GuideWorkflow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const confirmRef = useRef<((accepted: boolean) => void) | null>(null);
  const [portalContainer, setPortalContainer] =
    useState<HTMLDialogElement | null>(null);

  useEffect(() => {
    localStorage.removeItem(PERSISTENCE_KEY);
    return () => confirmRef.current?.(false);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const handleChange = (event: MediaQueryListEvent) =>
      setIsDesktop(event.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const updatePortal = () => {
      const { current } = workflowRef;
      const id =
        current?.screen === 'setup'
          ? 'shared-start-session-modal'
          : 'shared-preview-modal';
      setPortalContainer(
        enabled && current && current.screen !== 'library'
          ? document.querySelector<HTMLDialogElement>(`#${id}[open]`)
          : null
      );
    };
    updatePortal();
    const observer = new MutationObserver(updatePortal);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['open'],
      childList: true,
      subtree: true,
    });
    return () => observer.disconnect();
  }, [enabled, workflow]);

  const registerWorkflow = useCallback((next: GuideWorkflow) => {
    workflowRef.current = next;
    setWorkflow(next);
    return () => {
      if (workflowRef.current === next) {
        workflowRef.current = null;
        setWorkflow(null);
      }
    };
  }, []);

  const lifecycle = useMemo<GuideLifecycle>(
    () => ({
      markPageLoading: () => setPageReady(false),
      markPageReady: () => setPageReady(true),
      registerWorkflow,
    }),
    [registerWorkflow]
  );

  const beforeTransition = useCallback(
    async (event: ReguideTransitionContext<DashboardGuideTargetId>) => {
      if (event.intent === 'dismiss' || event.intent === 'complete') {
        setError(null);
        return true;
      }
      const { current } = workflowRef;
      if (!enabled || !current || current.busy) return false;
      setError(null);
      if (event.intent === 'restart') {
        if (current.hasSession) {
          const accepted = await new Promise<boolean>((resolve) => {
            const abort = () => confirmRef.current?.(false);
            const finish = (value: boolean) => {
              event.signal.removeEventListener('abort', abort);
              confirmRef.current = null;
              setConfirmation(false);
              resolve(value);
            };
            confirmRef.current = finish;
            event.signal.addEventListener('abort', abort, { once: true });
            setConfirmation(true);
          });
          if (!accepted || event.signal.aborted) return false;
          await current.cancelSetup('library', event.signal);
        } else {
          current.closePreview();
        }
      } else if (
        event.intent === 'back' &&
        event.currentStepId === GUIDE_STEPS.SCENARIO_LAUNCH &&
        current.hasSession
      ) {
        await current.cancelSetup('preview', event.signal);
      } else if (
        event.intent === 'back' &&
        event.currentStepId === GUIDE_STEPS.SCENARIO_PREVIEW
      ) {
        current.closePreview();
      }
      return !event.signal.aborted;
    },
    [enabled]
  );

  const steps = useMemo<ReguideStep<DashboardGuideTargetId>[]>(() => {
    const originalSteps: ReguideStep<DashboardGuideTargetId>[] = [
      {
        id: GUIDE_STEPS.WELCOME,
        title: `Welcome to Znario, ${user?.firstName ?? 'there'}!`,
        body: 'This short tutorial will familiarize you with your dashboard and get you ready to start running sessions with your team.',
      },
      {
        id: GUIDE_STEPS.HELP_GUIDE,
        title: 'Help Guide',
        body: 'Tap the question mark icon at anytime to reopen this walkthrough.',
      },
      {
        id: isDesktop
          ? GUIDE_STEPS.NAVIGATION_DESKTOP
          : GUIDE_STEPS.NAVIGATION_MOBILE,
        title: 'Navigation',
        placement: 'right',
        body: (
          <p>
            Access to core capabilities:
            <br />
            <br />
            <strong>Library:</strong>Choose and run scenarios.
            <br />
            <strong>Favorites:</strong> Add scenarios you want to come back to.
            <br />
            <strong>Reports:</strong> Access your session history.
            <br />
            <strong>Settings:</strong> Change your password.
          </p>
        ),
      },
      {
        id: GUIDE_STEPS.SEARCH,
        title: 'Search',
        body: 'Find scenarios by title, topic, or keyword.',
        focusScope: 'card-and-target',
      },
      {
        id: GUIDE_STEPS.SCENARIO_CARDS,
        title: 'Scenario Cards',
        body: 'Give you a quick summary of the scenario. Tap the star to save it in your Favorites. Tap preview to see more.',
        ...(workflow?.hasScenarios
          ? { mode: 'custom' as const, validator: () => false }
          : { mode: 'default' as const }),
        focusScope: 'card-and-target',
      },
      {
        id: GUIDE_STEPS.SCENARIO_PREVIEW,
        title: 'Scenario Preview Mode',
        body: 'Review the full scenario script and session plan. Tap items in the session plan for more detail. Tap Start Session to run the session.',
        ...(workflow?.hasScenarios && workflow.canStartSession
          ? { mode: 'custom' as const, validator: () => false }
          : { mode: 'default' as const }),
        placement: 'right',
        focusScope: 'card-and-target',
      },
      {
        id: GUIDE_STEPS.SCENARIO_LAUNCH,
        title: 'Scenario Launch Screen',
        body: 'If you are on a computer scan this QR code for mobile controls and anonymous messages. Co-hosts can also scan to help run the session.\nYou must approve your own request and others before tapping “Go To Session”.\n',
        placement: 'right',
        focusScope: 'card-and-target',
      },
    ];
    if (!error && !(guideOpen && confirmation)) return originalSteps;
    return originalSteps.map((step) =>
      'steps' in step
        ? step
        : {
            ...step,
            body: (
              <>
                {step.body}
                {error && (
                  <p role="alert" className="text-error">
                    {error}
                  </p>
                )}
                {guideOpen && confirmation && (
                  <ConfirmActionModal
                    open
                    title="Restart the tour?"
                    message="Restarting cancels the session you just created and returns to the beginning of the tour."
                    confirmLabel="Cancel session & restart"
                    cancelLabel="Keep session"
                    onConfirm={() => confirmRef.current?.(true)}
                    onCancel={() => confirmRef.current?.(false)}
                  />
                )}
              </>
            ),
          }
    );
  }, [
    confirmation,
    error,
    guideOpen,
    isDesktop,
    user?.firstName,
    workflow?.canStartSession,
    workflow?.hasScenarios,
  ]);

  return (
    <GuideLifecycleContext.Provider value={lifecycle}>
      <ReguideProvider
        steps={steps}
        portalContainer={portalContainer}
        beforeTransition={beforeTransition}
        onTransitionError={(cause) => {
          logger.error('Dashboard guide transition failed:', cause);
          setError('Unable to continue. Please try again.');
        }}
        escapeBehavior={confirmation ? 'delegate' : 'dismiss'}
        theme={{
          card: {
            background: 'var(--color-base-100)',
            border: '2px solid var(--color-primary)',
          },
          title: { color: 'var(--color-base-content)' },
          body: { color: 'var(--color-base-content)' },
          buttons: {
            primary: {
              background: 'var(--color-primary)',
              color: 'var(--color-primary-content)',
              border: '1px solid var(--color-primary)',
              borderRadius: 'var(--radius-field)',
            },
            secondary: {
              background: 'var(--color-base-200)',
              color: 'var(--color-base-content)',
              border: '1px solid var(--light-gray)',
              borderRadius: 'var(--radius-field)',
            },
          },
          highlight: { borderRadius: 'var(--radius-field)' },
        }}
      >
        <GuideRuntime
          enabled={enabled}
          pageReady={pageReady}
          workflow={workflow}
          portalContainer={portalContainer}
          onOpenChange={setGuideOpen}
        >
          {children}
        </GuideRuntime>
      </ReguideProvider>
      {!guideOpen && (
        <ConfirmActionModal
          open={confirmation}
          title="Restart the tour?"
          message="Restarting cancels the session you just created and returns to the beginning of the tour."
          confirmLabel="Cancel session & restart"
          cancelLabel="Keep session"
          onConfirm={() => confirmRef.current?.(true)}
          onCancel={() => confirmRef.current?.(false)}
        />
      )}
    </GuideLifecycleContext.Provider>
  );
};

export const useGuideLifecycle = () => {
  const context = useContext(GuideLifecycleContext);
  if (!context)
    throw new Error('useGuideLifecycle must be used within GuideProvider');
  return context;
};

export default GuideProvider;
